import { Link, useLocation } from "react-router-dom";

import { ChevronRight, Copy, Mail, X } from "lucide-react";
import classnames from "classnames";
import nookPayIcon from "@src/assets/Favicon.png";
import StatusChip from "@components/ui-components/status-chip";
import { toastify } from "@helpers/toast";

const Sidebar = (props) => {
  const { menuData = {}, isDesktop = true, menuVisibility, setMenuVisibility } = props;
  const { pathname } = useLocation();
  const items = menuData.panel || [];
  const label = menuData.label || "Workspace";
  const context = menuData.context;
  const contact = menuData.footer?.contact;

  // mailto: hands off to the OS's default mail app, which gives no visible feedback if
  // one isn't configured (looks "non-clickable"). Copy the address as a guaranteed-to-work
  // fallback and confirm the click did something either way.
  const handleContactClick = async (email) => {
    try {
      await navigator.clipboard.writeText(email);
      toastify({ title: `Opening your email app — we've also copied ${email} to your clipboard`, type: "success" });
    } catch {
      toastify({ title: "Opening your email app…", type: "success" });
    }
  };

  // Below the desktop breakpoint the sidebar is an off-canvas drawer driven by the
  // navbar's hamburger, so it has to sit above the fixed navbar (z-[1000]) rather than
  // under it. On desktop it stays a plain fixed column at its original stacking level.
  const closeMenu = () => setMenuVisibility?.(false);

  return (
    <>
      {!isDesktop && menuVisibility && (
        <div
          className="fixed inset-0 z-[1100] bg-black/50"
          onClick={closeMenu}
          aria-hidden="true"
        />
      )}
      <div
        className={classnames(
          "fixed inset-y-0 left-0 flex w-60 flex-col overflow-y-auto bg-sidebar text-sidebar-foreground transition-transform duration-200",
          isDesktop
            ? "z-[198] translate-x-0"
            : classnames("z-[1110] shadow-2xl", menuVisibility ? "translate-x-0" : "-translate-x-full")
        )}
      >
      <div className="flex items-center gap-3 border-b border-sidebar-border/60 px-5 py-3">
        <img
          src={nookPayIcon}
          alt="NookPay"
          className="h-9 w-9 rounded-xl object-cover ring-1 ring-white/10"
        />
        <span className="text-base font-semibold tracking-tight text-white">
          NookPay
        </span>
        {!isDesktop && (
          <button
            type="button"
            aria-label="Close navigation"
            onClick={closeMenu}
            className="ml-auto text-sidebar-foreground/70 hover:text-white"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>
      <div className="px-5 pb-2.5 pt-5 text-[10px] font-semibold uppercase tracking-[0.14em] text-sidebar-foreground/50">
        {label}
      </div>
      {context && (
        <div className="mx-3 mb-3 rounded-md bg-sidebar-accent/50 px-3 py-2.5">
          <div className="truncate text-sm font-semibold text-white">{context.name}</div>
          {context.paymentModelLabel && (
            <div className="mt-0.5 truncate text-[11px] text-sidebar-foreground/60">
              {context.paymentModelLabel}
            </div>
          )}
          <div className="mt-1.5">
            <StatusChip tone={context.kycTone} label={context.kycLabel} />
          </div>
        </div>
      )}
      <nav className="flex flex-col gap-0.5 px-3">
        {items.map((item) => {
          if (item.show === false) return null;
          const active = item.exact
            ? pathname === item.navLink
            : pathname.startsWith(item.navLink);
          const Icon = item.icon;
          return (
            <Link
              key={item.id}
              to={item.navLink}
              data-cy={`nav-${item.id}`}
              onClick={closeMenu}
              className={classnames(
                "group flex items-center gap-3 rounded-md px-3 py-2.5 text-[13px] font-medium transition",
                active
                  ? "bg-sidebar-accent text-white"
                  : "text-sidebar-foreground/80 hover:bg-sidebar-accent/60 hover:text-white"
              )}
            >
              {Icon && (
                <Icon
                  className={classnames(
                    "h-4 w-4 shrink-0",
                    active
                      ? "text-sidebar-primary"
                      : "text-sidebar-foreground/60 group-hover:text-sidebar-primary"
                  )}
                />
              )}
              <span className="truncate">{item.title}</span>
              {active && (
                <ChevronRight className="ml-auto h-3.5 w-3.5 text-sidebar-primary" />
              )}
            </Link>
          );
        })}
      </nav>
      <div className="mt-auto border-t border-sidebar-border/60 px-4 py-4">
        {contact && (
          <a
            href={`mailto:${contact.email}`}
            onClick={() => handleContactClick(contact.email)}
            className="group mb-3 flex items-start gap-3 rounded-md border border-sidebar-border/60 bg-sidebar-accent/30 p-3 transition hover:bg-sidebar-accent/50"
          >
            <div className="grid h-7 w-7 shrink-0 place-items-center rounded-md bg-sidebar-primary/20 text-sidebar-primary">
              <Mail className="h-3.5 w-3.5" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1 text-xs font-semibold text-sidebar-foreground">
                Contact us
                <Copy className="h-3 w-3 opacity-0 transition group-hover:opacity-100" />
              </div>
              <div className="truncate text-[11px] text-sidebar-foreground/60">
                {contact.email}
              </div>
              <div className="mt-1 text-[10px] leading-tight text-sidebar-foreground/50">
                Reach out externally for account or payout support.
              </div>
            </div>
          </a>
        )}
        <div className="px-1 text-[11px] text-sidebar-foreground/50">
          NookPay · {label}
        </div>
      </div>
      </div>
    </>
  );
};

export default Sidebar;
